import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCopy } from "@/contexts/CopyContext";
import {
  ArrowRight, Cake, Sparkles, HeartHandshake, BellRing, Baby, Users,
  Radio, Dumbbell, PartyPopper, Trophy, UserPlus, Calendar as CalIcon, Bell,
} from "lucide-react";
import { useEffect, useMemo } from "react";

/**
 * FEATURE_POOL — Steve (Feb 2026): every login, show a different mix of
 * what Born Day actually does. We pick 6 at random from this pool on
 * each visit so a returning visitor sees fresh ideas, not the same six
 * cards over and over.
 *
 * Keep entries punchy. Title in two-to-four words, body in ≤15 words.
 */
const FEATURE_POOL = [
  { icon: Radio,         title: "Go Live with family",         body: "Tap Go Live — everyone gets a Join In button. Real-time workouts, story-time, you name it." },
  { icon: Dumbbell,      title: "Schedule the workout",        body: "Recurring workout events with a one-tap Start Workout that goes live to the whole group." },
  { icon: Cake,          title: "Save every birthday forever", body: "5-year recurring birthdays. Add once, get reminded every single year — automatically." },
  { icon: HeartHandshake, title: "Plan a wedding together",    body: "Shared timeline, contact list, RSVPs. Less group-chat chaos, more actual planning." },
  { icon: Trophy,        title: "Throw a graduation party",     body: "Coordinate the venue, the food, the surprise reveal — one place, one source of truth." },
  { icon: PartyPopper,   title: "Surprise parties, simplified", body: "Private group → invite people → nobody slips up to the guest of honor." },
  { icon: Users,         title: "Family contact lists",         body: "Opt-in directory — everyone's number, email, and birthday in one tidy place." },
  { icon: Baby,          title: "Track how the baby is doing",  body: "Add far-flung relatives to a baby group — milestones, photos, updates land in their feed." },
  { icon: UserPlus,      title: "Reunions, finally",            body: "Send a link to that cousin you haven't seen in years — one tap and they're in the loop." },
  { icon: BellRing,      title: "Phone buzzes that arrive",     body: "Native push notifications even when Born Day is closed — same channel as Gmail." },
  { icon: CalIcon,       title: "See the next 5 years",          body: "Recurring view of every birthday, anniversary, milestone — no spreadsheet required." },
  { icon: Sparkles,      title: "One sign-up, every group",     body: "Join your spouse's family group, your hiking crew, your kid's school — all under one account." },
  { icon: Bell,          title: "Anniversary reminders, kept",   body: "Born Day quietly remembers what you forget. No more 'oh no, was that yesterday?' moments." },
];

/** Deterministic Fisher-Yates so we don't rely on Array.sort's stability. */
function pickRandom(arr, n) {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.slice(0, n);
}

export default function Landing() {
  const { user } = useAuth();
  const { t } = useCopy();
  const navigate = useNavigate();

  // Pick a fresh 6 of the 13 features for THIS visit. `useMemo` with an
  // empty deps array means we re-roll only on remount (i.e. each page
  // load / re-login), exactly what Steve asked for.
  const features = useMemo(() => pickRandom(FEATURE_POOL, 6), []);

  useEffect(() => {
    if (user && user.user_id) navigate("/app");
  }, [user, navigate]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <header className="px-4 sm:px-6 py-4 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-2 font-extrabold text-xl">
          <span
            className="w-9 h-9 grid place-items-center"
            style={{ background: "var(--primary)", color: "var(--primary-fg)",
              borderRadius: "var(--radius-btn)", border: "1px solid var(--border)" }}
          >
            <Cake size={18} />
          </span>
          <span style={{ letterSpacing: "-0.04em" }}>Born Day</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" className="bd-btn bd-btn-ghost" data-testid="landing-login">Sign in</Link>
          <Link to="/register" className="bd-btn" data-testid="landing-register">
            {t("landing.cta_primary", "Get started")} <ArrowRight size={16} />
          </Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-20 pb-14 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="bd-chip mb-6" data-testid="landing-tagline">
            <Sparkles size={12} /> {t("landing.eyebrow", "A standard, friendly calendar for the people you share life with")}
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] mb-6" style={{ letterSpacing: "-0.05em" }}>
            {t("landing.headline", "Born Day keeps every birthday, milestone, and plan in one place.")}
          </h1>
          <p className="text-lg sm:text-xl mb-8 max-w-2xl" style={{ color: "var(--text-dim)" }}>
            {t("landing.subhead",
              "Add the birthdays you don&apos;t want to miss. Plan parties, trips, and home gatherings. Track who&apos;s bringing what. Born Day works for grandparents, parents, and kids alike.")}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" className="bd-btn" data-testid="hero-register">
              {t("landing.cta_primary", "Get started")} <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="bd-btn bd-btn-ghost" data-testid="hero-login">
              {t("landing.cta_secondary", "I already have an account")}
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-2 text-sm" style={{ color: "var(--text-dim)" }}>
            <span className="bd-chip"><Cake size={10} /> Birthdays</span>
            <span className="bd-chip"><Baby size={10} /> Babies on the way</span>
            <span className="bd-chip"><HeartHandshake size={10} /> Fundraisers</span>
            <span className="bd-chip"><Users size={10} /> Groups</span>
            <span className="bd-chip"><BellRing size={10} /> Reminders</span>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="bd-surface p-6" data-testid="landing-preview">
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-lg">June 2026</div>
              <div className="flex gap-1">
                <span className="bd-cal-dot" style={{ background: "#FF8E72" }} />
                <span className="bd-cal-dot" style={{ background: "#9EA1D4" }} />
                <span className="bd-cal-dot" style={{ background: "#F4A6CD" }} />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5 text-center text-xs" style={{ color: "var(--text-dim)" }}>
              {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (<div key={`dow-${i}-${d}`}>{d}</div>))}
            </div>
            <div className="grid grid-cols-7 gap-1.5 mt-2">
              {Array.from({ length: 30 }, (_, i) => {
                const has = [3, 8, 14, 19, 22, 28].includes(i);
                const t2 = [3, 8, 14, 19, 22, 28].indexOf(i);
                const colors = ["#FF8E72", "#9EA1D4", "#F4A6CD", "#FF8E72", "#FFD60A", "#7CD9A8"];
                return (
                  <div
                    key={i}
                    className="aspect-square rounded-md flex flex-col items-center justify-center text-[10px] font-semibold"
                    style={{
                      background: has ? "color-mix(in srgb, " + colors[t2] + " 22%, transparent)" : "var(--bg-soft)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    <span>{i + 1}</span>
                    {has && <span className="bd-cal-dot mt-0.5" style={{ background: colors[t2] }} />}
                  </div>
                );
              })}
            </div>
            <div className="bd-divider my-4" />
            <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--text-dim)" }}>This month</div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center justify-between">
                <span><Cake size={11} className="inline mr-1" /> Mom&apos;s birthday</span>
                <span className="bd-mono text-xs" style={{ color: "var(--text-dim)" }}>Jun 14</span>
              </li>
              <li className="flex items-center justify-between">
                <span><Baby size={11} className="inline mr-1" /> We&apos;re having a baby — due</span>
                <span className="bd-mono text-xs" style={{ color: "var(--text-dim)" }}>Jun 19</span>
              </li>
              <li className="flex items-center justify-between">
                <span><HeartHandshake size={11} className="inline mr-1" /> School fundraiser</span>
                <span className="bd-mono text-xs" style={{ color: "var(--text-dim)" }}>Jun 22</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-3xl sm:text-4xl font-bold mb-10" style={{ letterSpacing: "-0.03em" }}>
          What you can do with Born Day.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <div key={f.title} className="bd-card" data-testid={`landing-feature-${i}`}>
              <span
                className="w-10 h-10 grid place-items-center mb-4"
                style={{ background: "var(--bg-soft)", border: "1px solid var(--border)", borderRadius: "var(--radius-btn)" }}
              >
                <f.icon size={18} />
              </span>
              <h3 className="text-xl font-bold mb-2" style={{ letterSpacing: "-0.02em" }}>{f.title}</h3>
              <p style={{ color: "var(--text-dim)" }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center">
        <h2 className="text-4xl sm:text-5xl font-extrabold mb-6" style={{ letterSpacing: "-0.04em" }}>
          {t("landing.cta_bottom_title", "One sign-up. The whole family in sync.")}
        </h2>
        <Link to="/register" className="bd-btn" data-testid="bottom-cta">
          {t("landing.cta_bottom_button", "Create your account")} <ArrowRight size={16} />
        </Link>
      </section>

      <footer className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center justify-between gap-4 text-sm" style={{ color: "var(--text-dim)" }}>
            <div className="flex items-center gap-2 font-bold">
              <Cake size={16} /> Born Day
            </div>
            <div className="bd-mono text-xs">v1.6</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
